/**
 * Utility functions for displaying friend names, usernames, and handles.
 * Standardizes the '@handle' convention across the app while keeping storage clean.
 */

/**
 * Formats a username as a handle (e.g. 'cruise_fan' -> '@cruise_fan').
 * If already prefixed with '@', returns as is. Returns empty string if null/undefined.
 */
export function formatFriendHandle(username?: string | null): string {
  if (!username) return '';
  const trimmed = username.trim();
  if (!trimmed) return '';
  return trimmed.startsWith('@') ? trimmed : `@${trimmed}`;
}

/**
 * Returns the primary display name for a friend or user object.
 * Priority:
 * 1. Formatted handle (@username) if friendUsername exists
 * 2. friendDisplayName (if provided and not an email/raw username)
 * 3. friendEmail (or email fallback)
 * 4. 'Unknown User'
 */
export function formatFriendDisplayName(friend?: {
  friendUsername?: string | null;
  friendDisplayName?: string | null;
  friendEmail?: string | null;
  fromUsername?: string | null;
  fromDisplayName?: string | null;
  fromEmail?: string | null;
  toUsername?: string | null;
  toDisplayName?: string | null;
  toEmail?: string | null;
  username?: string | null;
  displayName?: string | null;
  email?: string | null;
} | null): string {
  if (!friend) return 'Unknown User';

  const username =
    friend.friendUsername ??
    friend.fromUsername ??
    friend.toUsername ??
    friend.username;

  if (username && username.trim()) {
    return formatFriendHandle(username);
  }

  const displayName =
    friend.friendDisplayName ??
    friend.fromDisplayName ??
    friend.toDisplayName ??
    friend.displayName;

  if (displayName && displayName.trim()) {
    return displayName.trim();
  }

  const email =
    friend.friendEmail ??
    friend.fromEmail ??
    friend.toEmail ??
    friend.email;

  if (email && email.trim()) {
    return email.trim();
  }

  return 'Unknown User';
}

/**
 * Extracts a single uppercase initial letter for user avatars.
 * Safely strips any leading '@' or special characters so the avatar shows a letter (e.g. 'C' for '@captain_dan')
 * instead of the '@' symbol.
 */
export function getFriendInitial(friendOrName?: {
  friendUsername?: string | null;
  friendDisplayName?: string | null;
  friendEmail?: string | null;
  fromUsername?: string | null;
  fromDisplayName?: string | null;
  fromEmail?: string | null;
  toUsername?: string | null;
  toDisplayName?: string | null;
  toEmail?: string | null;
  username?: string | null;
  displayName?: string | null;
  email?: string | null;
} | string | null): string {
  if (!friendOrName) return '?';

  let raw = '';
  if (typeof friendOrName === 'string') {
    raw = friendOrName;
  } else {
    raw =
      friendOrName.friendUsername ??
      friendOrName.fromUsername ??
      friendOrName.toUsername ??
      friendOrName.username ??
      friendOrName.friendDisplayName ??
      friendOrName.fromDisplayName ??
      friendOrName.toDisplayName ??
      friendOrName.displayName ??
      friendOrName.friendEmail ??
      friendOrName.fromEmail ??
      friendOrName.toEmail ??
      friendOrName.email ??
      '?';
  }

  // Strip leading '@' or non-alphanumeric prefix
  const cleaned = raw.trim().replace(/^[@\s]+/, '');
  if (!cleaned) return '?';

  return cleaned.charAt(0).toUpperCase();
}
